import * as React from "react";
import {
  useDispatch,
  useMappedState
} from "redux-react-hook";
import * as Sentry from "@sentry/react";

import { amplifyConfigure } from "src/utils/aws-amplify/configure";
import {
  setAppSettings,
  setGlobalError
} from "src/core/shared/store/global/global.actions";
import { IAppSettings } from "src/core/shared/store/global/global.reducer";
import { IShutdown } from "src/modules/shutdowns/api/shutdown.api";
import { IStore } from "src/core/shared/store/index";
import { formatShutdownDates } from "src/modules/shutdowns/utils/formatShutdownDates";

import {
  GetGlobalSettingsParamsType,
  useGlobalSettingsApi
} from "../api/globalSettings.api";

interface IState {
  isLoading: boolean;
  isAWSConfigured: boolean;
  globalSettings: IAppSettings | null;
  error: boolean;
}

interface IActions {
  getGlobalSettings: (setting: GetGlobalSettingsParamsType) => void;
  checkGlobalSettingsAreSynced: () => void;
}

const useGlobalSettings = (): [IState, IActions] => {
  const dispatch = useDispatch();
  const [ isLoading, setLoading ] = React.useState<boolean>(false);
  const [ isAWSConfigured, setAWSConfigured ] = React.useState<boolean>(false);
  const [ settings, globalSettingsApi ] = useGlobalSettingsApi();
  const { user } = useMappedState(({ global }: IStore) => ({ user: global.user }));

  const [ getGlobalSettingsResponse, getGlobalSettings ] = globalSettingsApi<Omit<IAppSettings, "shutdowns"> & { shutdowns?: IShutdown[] }>(
    "GET_CLIENT_SETTINGS"
  );

  const [ error, setError ] = React.useState<boolean>(false);

  const getSettings = (setting: GetGlobalSettingsParamsType) => {
    if (!isLoading && !error) {
      setLoading(true);
      getGlobalSettings(setting);
    }
  };

  const checkGlobalSettingsAreSynced = () => {
    if (settings) {
      // Resync global settings every half hour
      setLoading(true);
      getGlobalSettings(null);
    }
  };

  // On mount
  React.useEffect(() => {
    // If the settings already exist in the store, configure the Amplify client.
    if (!isLoading && settings && settings.aws) {
      amplifyConfigure({
        Auth: {
          identityPoolId: settings.aws.cognito.identityPoolId,
          region: settings.aws.cognito.region,
          userPoolId: settings.aws.cognito.userPoolId,
          userPoolWebClientId: settings.aws.cognito.clientId
        },
        Storage: {
          AWSS3: {
            region: settings.aws.s3.region,
            bucket: settings.aws.s3.bucket
          }
        }
      });
      setAWSConfigured(true);
    }
  }, [ isLoading, settings ]);

  // On getGlobalSettingsResponse
  React.useEffect(() => {
    // On error
    if (getGlobalSettingsResponse.error) {
      if (process.env.REACT_APP_ENVIRONMENT !== "dev") {
        Sentry.captureException(getGlobalSettingsResponse.error, user ? {
          user: {
            id: user.id.toString(),
            email: user.email
          }
        } : {});
      }

      // Update loading state
      setLoading(false);
      setError(true);
      // Dispatch the error
      dispatch(setGlobalError(getGlobalSettingsResponse.error));
    }

    // On response
    if (getGlobalSettingsResponse.data) {
      // Update loading state
      setLoading(false);

      // Update the Amplify client configuration
      amplifyConfigure({
        Auth: {
          identityPoolId: getGlobalSettingsResponse.data.aws.cognito.identityPoolId,
          region: getGlobalSettingsResponse.data.aws.cognito.region,
          userPoolId: getGlobalSettingsResponse.data.aws.cognito.userPoolId,
          userPoolWebClientId: getGlobalSettingsResponse.data.aws.cognito.clientId
        },
        Storage: {
          AWSS3: {
            region: getGlobalSettingsResponse.data.aws.s3.region,
            bucket: getGlobalSettingsResponse.data.aws.s3.bucket
          }
        }
      });
      setAWSConfigured(true);

      const { shutdowns, ...rest } = getGlobalSettingsResponse.data;
      const shutdownDates = formatShutdownDates(shutdowns || []);

      dispatch(
        setAppSettings({
          ...rest,
          shutdowns: shutdowns ? shutdownDates : null
        }, true)
      );
    }
  }, [
    dispatch,
    formatShutdownDates,
    getGlobalSettingsResponse
  ]);

  return [
    {
      isLoading,
      isAWSConfigured,
      globalSettings: settings,
      error
    },
    {
      getGlobalSettings: getSettings,
      checkGlobalSettingsAreSynced
    }
  ];
};

export default useGlobalSettings;
